import { InlineNotification } from '@carbon/react';
import {
  Badge,
  Catalog,
  Dashboard,
  Enterprise,
  Location,
  Network_3,
  Receipt,
  Report,
  Restaurant,
  Ticket,
  Settings,
  ShoppingCart,
  TableSplit,
  UserMultiple,
  Globe,
} from '@carbon/icons-react';
import {
  AdminProvider,
  AdminShell,
  hasPermission,
  kitchenNav,
  kitchenRoutes,
  orderRoutes,
  salesNav,
  LoginPage,
  resourceNav,
  ListScreen,
  resourceRoutes,
  RuleBuilder,
  type FieldEditorProps,
  type NavItem,
  type ResourceExtension,
  type RecordValues,
  within,
} from '@pos/admin-ui';
import { currentLanguage, setLanguage, useTranslation, type Language } from '@pos/i18n';
import { useMemo, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { addressLookup, addressSource, hoApi, isAddressLookupEnabled, TENANT_CODE, useSession } from './api';
import { HoOverview } from './overview/HoOverview';
import { NAV_GROUPS, RESOURCES } from './generated/resources';
import { HO_RESOURCES } from './catalog/resourceOverrides';
import { SetPasswordButton } from './staff/SetPasswordButton';
import { DuplicateButton } from './common/DuplicateButton';
import { StoreSalesChannelsTab } from './channelStores/StoreSalesChannelsTab';
import { StoreReservationsTab } from './reservations/StoreReservationsTab';
import { StoreConnectionTab } from './sync/StoreConnectionTab';
import { QuickSetupPage } from './tables/QuickSetupPage';
import { CopyShiftsToolbar } from './pos/CopyShiftsToolbar';
import { PosButtonsToolbar } from './pos/PosButtonsToolbar';
import { GenerateCardsButton } from './loyalty/GenerateCardsButton';
import { MemberOrdersButton } from './loyalty/MemberOrdersButton';
import { MemberPointsPage } from './loyalty/MemberPointsPage';
import { MemberPointsTab } from './loyalty/MemberPointsTab';
import { MemberRewardsTab } from './loyalty/MemberRewardsTab';
import { PluginChoice, PluginSettings } from './loyalty/RewardProgramFields';
import { PageContentField } from './content/ContentEditors';
import { PagePublishActions, PageVersionsTab } from './content/PagePublishing';
import { SegmentActions, SegmentConditionsField, SegmentListTab, SegmentPeopleTab } from './crm/SegmentScreens';
import { IssueRewardsPage } from './loyalty/IssueRewardsPage';
import { PosProfileSyncTab } from './pos/PosProfileSyncTab';
import { CsvDownloadButton } from './promotions/CsvDownloadButton';
import { ProfileSettingsEditor } from './pos/ProfileSettingsEditor';
import { MenuEditorTab } from './menus/MenuEditorTab';
import { LocationDatasetsPage } from './locations/LocationDatasetsPage';
import { DeliveryProvidersPage, ProviderAccountsPage } from './delivery/DeliveryProvidersPage';
import { EinvoiceDetailPage } from './einvoice/EinvoiceDetailPage';
import { EinvoicesPage } from './einvoice/EinvoicesPage';
import { DeliveryServicesPage } from './delivery/DeliveryServicesPage';
import { FlowPresetToolbar } from './flows/FlowPresetToolbar';
import { FlowStepsTab } from './flows/FlowStepsTab';
import { PriceListMenusTab } from './pricing/PriceListMenusTab';
import { RolePermissionsTab } from './staff/RolePermissionsTab';
import { RoleHoldersTab } from './staff/RoleHoldersTab';
import { RoleTemplateButton } from './staff/RoleTemplateButton';
import { ItemGroupVariantsTab } from './catalog/itemGroups/ItemGroupVariantsTab';
import { SuggestGroupsButton } from './catalog/itemGroups/SuggestGroupsButton';
import { OptionItemButton } from './options/OptionItemButton';
import { PriceTableTab } from './pricing/PriceTableTab';
import { AuditLogPage } from './logs/AuditLogPage';
import { AuditSettingsPage } from './logs/AuditSettingsPage';
import { CustomerSecurityPage } from './customerSecurity/CustomerSecurityPage';
import { SsoSettingsPage } from './signIn/SsoSettingsPage';
import { StaffSignInTab } from './signIn/StaffSignInTab';
import { TwoStepPage } from './signIn/TwoStepPage';
import { useHoLogin } from './signIn/useHoLogin';
import { IntegrationLogsPage } from './logs/IntegrationLogsPage';
import { SyncLogsPage } from './logs/SyncLogsPage';
import { StoreConnectionsPage } from './sync/StoreConnectionsPage';
import { BrandingPage } from './branding/BrandingPage';
import { MasterDataPublishPage } from './sync/MasterDataPublishPage';
import { TemplateBodyEditor } from './templates/TemplateBodyEditor';
import { ThemeField } from './brandSites/ThemeField';
import { ImageField } from './images/ImagePicker';
import { ImagesPage } from './images/ImagesPage';
import { imagesNavPermission } from './images/imagesApi';
import { CustomerMessagesPage } from './messaging/CustomerMessagesPage';
import { CustomerAccountPage } from './customerAccounts/CustomerAccountPage';
import { CustomerAccountsPage } from './customerAccounts/CustomerAccountsPage';
import { OnlineOrderPage } from './onlineOrders/OnlineOrderPage';
import { SectionListFilterField } from './pos/SectionListFilterField';
import { OnlineOrdersPage } from './onlineOrders/OnlineOrdersPage';
import { ORDERS_READ } from './onlineOrders/onlineOrdersApi';
import { FormSubmissionPage } from './formSubmissions/FormSubmissionPage';
import { FormSubmissionsPage } from './formSubmissions/FormSubmissionsPage';
import { SUBMISSIONS_PERMISSION } from './formSubmissions/formSubmissionsApi';
import { ACCOUNTS_READ } from './customerAccounts/customerAccountsApi';
import { MessageTemplatesPage } from './messaging/MessageTemplatesPage';
import { TableQrPage } from './tableQr/TableQrPage';
import { TABLE_QR_READ } from './tableQr/tableQrApi';
import { TranslationsPage } from './catalog/translations/TranslationsPage';
import { ImportPage } from './catalog/import/ImportPage';
import { ImportLinkButton } from './catalog/import/ImportLinkButton';
import { IMPORT_MANAGE } from './catalog/import/importApi';
import { AddChannelStoresButton } from './channelStores/AddChannelStoresButton';
import { PaymentProfilesPage } from './payments/PaymentProfilesPage';
import { PaymentReconciliationPage } from './payments/PaymentReconciliationPage';
import { HourlyReportPage, PaymentsTaxReportPage, ProductMixPage, SalesReportPage } from './reports/ReportPages';
import { ControlsReportPage } from './reports/ControlsReportPage';
import { CustomersReportPage } from './reports/CustomersReportPage';
import { EinvoiceReconPage } from './reports/EinvoiceReconPage';
import { JournalReportPage } from './reports/JournalReportPage';
import { MenuEngineeringPage } from './reports/MenuEngineeringPage';
import { OperationsReportPage } from './reports/OperationsReportPage';
import { QrReconciliationPage } from './reports/QrReconciliationPage';
import { StaffReportPage } from './reports/StaffReportPage';
import { REPORT_PATHS } from './reports/reportRange';
import {
  HoStockBalancesPage,
  HoStockCountsPage,
  HoStockMovementsPage,
  HoStockTransfersPage,
} from './stock/HoStockPages';
import { HO_STOCK_PATHS } from './stock/stockPaths';
import { getTextLanguages, translationKeys, TRANSLATIONS_READ } from './catalog/translations/translationsApi';

/** CMS › Forms: the pages of kind form, edited like any page (CMS phase 2). */
const PAGES_DEF = HO_RESOURCES['cms.page'];
const FORMS_DEF = PAGES_DEF && { ...PAGES_DEF, title: 'cms.forms.title' as const, route: '/cms/forms' };
const FORMS_FIXED = { kind: 'form' } as const;

/** Each record's History tab reads HO's audit log (ADR 0118). */
const HO_HISTORY = { path: '/api/v1/identity/audit-log', permission: 'ho.audit.read' };

const NAV: NavItem[] = [
  { label: 'admin.nav.overview', path: '/', icon: Dashboard },
  salesNav('ho'),
  // HO's reports across stores and periods (ADR 0097).
  {
    label: 'admin.nav.reports',
    icon: Report,
    items: [
      { label: 'admin.nav.reportSales', path: REPORT_PATHS.sales, permission: 'ho.reports.sales.read' },
      { label: 'admin.nav.reportProducts', path: REPORT_PATHS.products, permission: 'ho.reports.sales.read' },
      { label: 'admin.nav.reportMenu', path: REPORT_PATHS.menu, permission: 'ho.reports.sales.read' },
      { label: 'admin.nav.reportHours', path: REPORT_PATHS.hours, permission: 'ho.reports.sales.read' },
      { label: 'admin.nav.reportPayments', path: REPORT_PATHS.payments, permission: 'ho.reports.finance.read' },
      { label: 'admin.nav.reportQr', path: REPORT_PATHS.qr, permission: 'ho.reports.finance.read' },
      { label: 'admin.nav.reportControls', path: REPORT_PATHS.controls, permission: 'ho.reports.staff.read' },
      { label: 'admin.nav.reportStaff', path: REPORT_PATHS.staff, permission: 'ho.reports.staff.read' },
      { label: 'admin.nav.reportOperations', path: REPORT_PATHS.operations, permission: 'ho.reports.sales.read' },
      { label: 'admin.nav.reportCustomers', path: REPORT_PATHS.customers, permission: 'ho.reports.sales.read' },
      { label: 'admin.nav.reportJournal', path: REPORT_PATHS.journal, permission: 'ho.reports.finance.read' },
      { label: 'admin.nav.reportEinvoices', path: REPORT_PATHS.einvoices, permission: 'ho.einvoice.read' },
      { label: 'admin.nav.reportStockBalances', path: HO_STOCK_PATHS.balances, permission: 'ho.stock.read' },
      { label: 'admin.nav.reportStockMovements', path: HO_STOCK_PATHS.movements, permission: 'ho.stock.read' },
      { label: 'admin.nav.reportStockTransfers', path: HO_STOCK_PATHS.transfers, permission: 'ho.stock.read' },
      { label: 'admin.nav.reportStockCounts', path: HO_STOCK_PATHS.counts, permission: 'ho.stock.read' },
    ],
  },
  kitchenNav('ho'),
  // Tables: quick setup first, before the generated lists (FR-TBL-01).
  // Delivery services have their own screen (below), so the generated list is not in the menu.
  ...resourceNav(
    Object.fromEntries(Object.entries(RESOURCES).filter(([key]) => key !== 'config.delivery_service')),
    NAV_GROUPS,
    {
      org: Enterprise,
      catalog: Catalog,
      menu: Restaurant,
      pos: Settings,
      tables: TableSplit,
      geo: Location,
      promotions: Ticket,
      loyalty: Badge,
      einvoice: Receipt,
      online: ShoppingCart,
      marketing: UserMultiple,
      cms: Globe,
    },
  )
    .map((item) =>
      'items' in item && item.label === 'md.groups.pos'
        ? {
            ...item,
            items: [
              ...item.items,
              {
                label: 'admin.nav.deliveryServices' as const,
                path: '/delivery/services',
                permission: 'ho.config.delivery_services.read',
              },
              {
                label: 'admin.nav.deliveryProviders' as const,
                path: '/delivery/providers',
                permission: 'ho.config.delivery_services.read',
              },
              // Payments through providers, for every channel (ADR 0074): next to the payment methods.
              {
                label: 'admin.nav.paymentAccounts' as const,
                path: '/payments/accounts',
                permission: 'ho.config.payment_methods.read',
              },
              {
                label: 'admin.nav.paymentProfiles' as const,
                path: '/payments/profiles',
                permission: 'ho.config.payment_methods.read',
              },
              {
                label: 'admin.nav.paymentReconciliation' as const,
                path: '/payments/reconciliation',
                permission: 'ho.orders.read',
              },
            ],
          }
        : item,
    )
    .map((item) =>
      'items' in item && item.label === 'md.groups.geo'
        ? {
            ...item,
            items: [
              ...item.items,
              {
                label: 'admin.nav.locationImport' as const,
                path: '/locations/import',
                permission: 'ho.locations.imports.read',
              },
            ],
          }
        : item,
    )
    // E-invoices (ADR 0048): the invoices HO issues and the provider accounts, beside the generated profiles.
    .map((item) =>
      'items' in item && item.label === 'md.groups.einvoice'
        ? {
            ...item,
            items: [
              { label: 'admin.nav.einvoices' as const, path: '/einvoice/invoices', permission: 'ho.einvoice.read' },
              ...item.items,
              {
                label: 'admin.nav.einvoiceProviders' as const,
                path: '/einvoice/providers',
                permission: 'ho.einvoice.read',
              },
            ],
          }
        : item,
    )
    .map((item) =>
      'items' in item && item.label === 'md.groups.catalog'
        ? {
            ...item,
            items: [
              ...item.items,
              {
                label: 'admin.nav.translations' as const,
                path: '/catalog/translations',
                permission: TRANSLATIONS_READ,
              },
              // One workbook for categories, items, options, prices, menus and POS buttons; any one manage permission.
              {
                label: 'admin.nav.menuImport' as const,
                path: '/catalog/import',
                permission: IMPORT_MANAGE,
              },
            ],
          }
        : item,
    )
    .map((item) =>
      'items' in item && item.label === 'md.groups.loyalty'
        ? {
            ...item,
            items: [
              ...item.items,
              {
                label: 'admin.nav.memberPoints' as const,
                path: '/loyalty/points',
                permission: 'ho.members.accounts.read',
              },
              // The same coupon or gift voucher to a list of members (ADR 0079).
              {
                label: 'admin.nav.issueRewards' as const,
                path: '/loyalty/issue-rewards',
                permission: 'ho.members.accounts.manage',
              },
            ],
          }
        : item,
    )
    .map((item) =>
      'items' in item && item.label === 'md.groups.tables'
        ? {
            ...item,
            items: [
              {
                label: 'admin.nav.tableQuickSetup' as const,
                path: '/tables/quick-setup',
                permission: 'ho.tables.floor_plans.manage',
              },
              ...item.items,
            ],
          }
        : item,
    ),
  {
    label: 'admin.nav.system',
    icon: Network_3,
    items: [
      { label: 'admin.nav.masterDataPublish', path: '/sync/publish', permission: 'ho.sync.read' },
      { label: 'admin.nav.storeConnections', path: '/sync/stores', permission: 'ho.sync.read' },
      { label: 'admin.nav.syncLogs', path: '/sync/logs', permission: 'ho.sync.read' },
      { label: 'admin.nav.integrationLogs', path: '/integration/logs', permission: 'ho.integration.logs.read' },
      { label: 'admin.nav.auditLog', path: '/audit', permission: 'ho.audit.read' },
      // Settings for the whole system: the platform note on every app's sign-in screen and footer (ADR 0106), staff and
      // customer sign-in, and how long the audit log is kept.
      { label: 'admin.nav.branding', path: '/org/branding', permission: 'ho.org.branding.read' },
      { label: 'admin.nav.signIn', path: '/org/sign-in', permission: 'ho.org.sign_in.read' },
      {
        label: 'admin.nav.customerSecurity',
        path: '/online/customer-security',
        permission: 'ho.online.customer_security.read',
      },
      { label: 'admin.nav.auditSettings', path: '/org/audit-settings', permission: 'ho.org.audit_settings.read' },
    ],
  },
];

/**
 * Online ordering's own screens: the website and app orders first (FR-ONL-05), table QR codes (FR-QR-07, ADR 0067) and customer
 * messaging (ADR 0072).
 */
function withOnline(nav: NavItem[]): NavItem[] {
  return nav.map((item) =>
    'items' in item && item.label === 'md.groups.online'
      ? {
          ...item,
          items: [
            { label: 'admin.nav.onlineOrders' as const, path: '/online/orders', permission: ORDERS_READ },
            // The website's and app's customer accounts (ADR 0082), beside their orders.
            { label: 'admin.nav.customerAccounts' as const, path: '/online/customers', permission: ACCOUNTS_READ },
            ...item.items,
            { label: 'admin.nav.tableQr' as const, path: '/online/table-qr', permission: TABLE_QR_READ },
            {
              label: 'admin.nav.messageTemplates' as const,
              path: '/online/message-templates',
              permission: TABLE_QR_READ,
            },
            {
              label: 'admin.nav.customerMessages' as const,
              path: '/online/customer-messages',
              permission: TABLE_QR_READ,
            },
            {
              label: 'admin.nav.messagingAccounts' as const,
              path: '/online/messaging-accounts',
              permission: TABLE_QR_READ,
            },
          ],
        }
      : item,
  );
}

/**
 * CMS (ADR 0081): content for the websites and, later, the apps. Next to the pages: forms (pages of kind form, placed on pages
 * with a form block), what visitors sent through them (CMS phase 2), and the uploaded images (FR-ONL-17, ADR 0066). The images
 * page opens with any of three read permissions and a nav link checks one, so the link checks the one the user has.
 */
function withCms(nav: NavItem[], imagesPermission: string): NavItem[] {
  return nav.map((item) =>
    'items' in item && item.label === 'md.groups.cms'
      ? {
          ...item,
          items: [
            ...item.items,
            { label: 'admin.nav.cmsForms' as const, path: '/cms/forms', permission: 'ho.content.read' },
            {
              label: 'admin.nav.formSubmissions' as const,
              path: '/cms/submissions',
              permission: SUBMISSIONS_PERMISSION,
            },
            { label: 'admin.nav.images' as const, path: '/cms/images', permission: imagesPermission },
          ],
        }
      : item,
  );
}

/** An image's address can be chosen from the uploaded images, or uploaded on the spot. */
const imageEditors = (...keys: string[]): Record<string, (props: FieldEditorProps) => ReactNode> =>
  Object.fromEntries(keys.map((key) => [key, (props: FieldEditorProps) => <ImageField {...props} />]));

export function App() {
  const { t } = useTranslation();
  const session = useSession((s) => s.session);
  const hoLogin = useHoLogin();
  const nav = useMemo(() => withCms(withOnline(NAV), imagesNavPermission(session ?? undefined)), [session]);
  // Address search through the maps provider in address forms, when HO has one set up (Maps:Provider).
  const addressLookupEnabled = useQuery({
    queryKey: ['ho', 'address-lookup-enabled'],
    queryFn: isAddressLookupEnabled,
    enabled: Boolean(session && hasPermission(session, `${within('ho.org')}|${within('ho.online')}`)),
    staleTime: Infinity,
  });
  // Translated fields show the staff languages plus the brand websites' (ADR 0071); the staff ones until this answers.
  const textLanguages = useQuery({
    queryKey: translationKeys.languages,
    queryFn: getTextLanguages,
    enabled: !!session,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const extensions = useMemo<Record<string, ResourceExtension>>(
    () => ({
      'org.app_user': {
        actions: (staff) => <SetPasswordButton staff={staff} />,
        // How they sign in at HO (ADR 0121): two-step sign-in, the company's provider, break-glass.
        tabs: [{ label: t('signIn.staff.tab'), render: (staff) => <StaffSignInTab staff={staff} /> }],
      },
      // The brand's logo on the POS, KDS and store admin sign-in screens (ADR 0106), apart from its website's.
      'org.brand': { fieldEditors: imageEditors('logoUrl') },
      // How the store connects to HO (ADR 0014), where it sells (FR-ONL-07) and its table bookings; each tab only for users who may see it.
      'org.store': {
        tabs: [
          ...(hasPermission(session ?? undefined, 'ho.sync.read')
            ? [{ label: t('admin.sync.tab'), render: (store: RecordValues) => <StoreConnectionTab store={store} /> }]
            : []),
          ...(hasPermission(session ?? undefined, 'ho.online.channel_stores.read')
            ? [
                {
                  label: t('salesChannels.tab'),
                  render: (store: RecordValues) => <StoreSalesChannelsTab store={store} />,
                },
              ]
            : []),
          // Table bookings (ADR 0092): one set of rules per store, for every channel and the POS.
          ...(hasPermission(session ?? undefined, 'ho.tables.reservation_settings.read')
            ? [
                {
                  label: t('reservations.tab'),
                  render: (store: RecordValues) => <StoreReservationsTab store={store} />,
                },
              ]
            : []),
        ],
      },
      // Profiles and their layouts reach stores only when synced (ADR 0016).
      // A role's permissions are one list of switches, not rows added one by one.
      // Roles (ADR 0116): permissions as a grid, from a template or a copy, and who holds each one.
      'org.role': {
        listToolbar: () => <RoleTemplateButton />,
        actions: (role) => <DuplicateButton resource="org.role" record={role} />,
        childTabs: { 'org.role_permission': (role) => <RolePermissionsTab role={role} /> },
        tabs: [{ label: t('roleHolders.title'), render: (role) => <RoleHoldersTab role={role} /> }],
      },
      // An option is an item (ADR 0029): the Item type filter lists the option items, and a new option item is created
      // from the item list or straight into an option group.
      'catalog.item': {
        listToolbar: (list) => (
          <>
            <OptionItemButton refresh={list.refresh} />
            <ImportLinkButton />
          </>
        ),
        fieldEditors: imageEditors('imageUrl'),
      },
      // An item group's variants are edited as a whole; groups can be suggested from item names (ADR 0052).
      'catalog.item_group': {
        listToolbar: (list) => <SuggestGroupsButton refresh={list.refresh} />,
        tabs: [{ label: t('itemGroups.tab'), render: (group) => <ItemGroupVariantsTab group={group} /> }],
        fieldEditors: imageEditors('imageUrl'),
      },
      // The website's logo, icon and image for shared links (FR-ONL-17).
      // ...and its theme as fields with a live preview, built by the website's own token generator.
      'config.brand_site': {
        fieldEditors: {
          ...imageEditors('logoUrl', 'iconUrl', 'ogImageUrl'),
          theme: (props: FieldEditorProps) => <ThemeField {...props} />,
        },
      },
      // Deals, their components and choices can come from the menu data workbook (ADR 0130).
      'catalog.deal': {
        listToolbar: () => <ImportLinkButton />,
      },
      'catalog.modifier_group': {
        childToolbars: {
          'catalog.modifier': (group, list) => <OptionItemButton group={group} refresh={list.refresh} />,
        },
      },
      // Prices are edited as one table, grouped into items (options included) and deals (FR-MENU-06).
      'menu.price_list': {
        listToolbar: () => <ImportLinkButton />,
        actions: (list) => <DuplicateButton resource="menu.price_list" record={list} />,
        childTabs: {
          'menu.price_list_entry': (list) => <PriceTableTab priceList={list} />,
          'menu.menu_price_list': (list) => <PriceListMenusTab priceList={list} />,
        },
      },
      // A menu's pages and items are edited as a whole (FR-MENU-01..05).
      'menu.menu': {
        listToolbar: () => <ImportLinkButton />,
        actions: (menu) => <DuplicateButton resource="menu.menu" record={menu} />,
        childTabs: { 'menu.menu_entry': (menu) => <MenuEditorTab menu={menu} /> },
      },
      // Steps are edited as one list, and a new flow can start from a preset (FR-CFG-02, ADR 0024).
      'config.order_flow': {
        listToolbar: () => <FlowPresetToolbar />,
        childTabs: { 'config.order_flow_step': (flow) => <FlowStepsTab flow={flow} /> },
      },
      // When a charge applies and a preset can be used, built from conditions rather than typed as JSON (ADR 0039).
      // The rules on a tab of their own: the builder needs the room. Large forms open on their own page (usesFormPage).
      'config.charge_type': {
        fieldTabs: ['applyRules'],
        fieldEditors: { applyRules: (field) => <RuleBuilder {...field} kind="charge" /> },
      },
      'config.discount_preset': {
        fieldTabs: ['rules'],
        fieldEditors: { rules: (field) => <RuleBuilder {...field} kind="preset" /> },
      },
      // Coupons (ADR 0043): when a code may be used, the same rule builder; its codes as a CSV to print or send.
      'promotions.promotion': {
        fieldTabs: ['rules'],
        fieldEditors: { rules: (field) => <RuleBuilder {...field} kind="promotion" /> },
        actions: (promotion) => (
          <CsvDownloadButton
            label={t('promotions.downloadCodes')}
            path="/api/v1/promotions/promotions/{promotionId}/codes.csv"
            params={{ promotionId: String(promotion.id) }}
            fileName={`codes-${String(promotion.code)}.csv`}
          />
        ),
      },
      // Gift vouchers (ADR 0043): a batch issues its vouchers; their codes as a CSV to print.
      'membership.gift_voucher_batch': {
        fieldTabs: ['rules'],
        fieldEditors: { rules: (field) => <RuleBuilder {...field} kind="voucher" /> },
        actions: (batch) => (
          <CsvDownloadButton
            label={t('promotions.downloadVouchers')}
            path="/api/v1/membership/gift-voucher-batches/{batchId}/vouchers.csv"
            params={{ batchId: String(batch.id) }}
            fileName={`vouchers-${String(batch.id)}.csv`}
          />
        ),
      },
      // Loyalty (ADR 0044): when a point rule earns, in the rule builder (only what HO knows of an order); an account's points.
      'membership.member_club': { actions: (club) => <GenerateCardsButton club={club} /> },
      // An order list section's filter as a choice of sale types, not JSON (FR-CFG-04).
      'config.pos_layout_section': {
        formPage: false, // a small form: it stays in the dialog
        fieldEditors: { listFilter: (field) => <SectionListFilterField {...field} /> },
      },
      'membership.point_rule': {
        fieldTabs: ['rules'],
        fieldEditors: { rules: (field) => <RuleBuilder {...field} kind="points" /> },
      },
      // Website content (ADR 0081): content as fields (site settings or blocks), publishing and versions.
      'cms.page': {
        fieldTabs: ['content'],
        fieldEditors: {
          content: (field) => <PageContentField {...field} />,
          coverUrl: (props: FieldEditorProps) => <ImageField {...props} />,
          ogImageUrl: (props: FieldEditorProps) => <ImageField {...props} />,
        },
        actions: (page) => <PagePublishActions page={page} />,
        tabs: [{ label: t('cms.versions.tab'), render: (page) => <PageVersionsTab page={page} /> }],
      },
      // Customer segments (ADR 0080): conditions in the rule builder with an estimate; who is in it, its list, refresh now.
      'crm.segment': {
        fieldTabs: ['conditions'],
        fieldEditors: { conditions: (field) => <SegmentConditionsField {...field} /> },
        actions: (segment) => <SegmentActions segment={segment} />,
        tabs: [
          { label: t('crm.segments.people.tab'), render: (segment) => <SegmentPeopleTab segment={segment} /> },
          { label: t('crm.segments.list.tab'), render: (segment) => <SegmentListTab segment={segment} /> },
        ],
      },
      // Rewards programs (ADR 0079): the trigger and the reward chosen from what HO knows, their settings as fields, and the
      // member conditions in the rule builder, on a tab of their own.
      'membership.reward_program': {
        fieldTabs: ['conditions'],
        fieldEditors: {
          triggerType: (field) => <PluginChoice {...field} kind="triggers" />,
          triggerSettings: (field) => <PluginSettings {...field} kind="triggers" />,
          rewardType: (field) => <PluginChoice {...field} kind="rewards" />,
          rewardSettings: (field) => <PluginSettings {...field} kind="rewards" />,
          conditions: (field) => <RuleBuilder {...field} kind="reward" />,
        },
      },
      'membership.member_account': {
        // The member's orders: the order list, filtered to their account (ADR 0044).
        actions: (account) => <MemberOrdersButton account={account} />,
        tabs: [
          { label: t('loyalty.points.tab'), render: (account) => <MemberPointsTab account={account} /> },
          // Coupons and gift vouchers issued to the member (ADR 0079).
          { label: t('loyalty.rewards.tab'), render: (account) => <MemberRewardsTab account={account} /> },
        ],
      },
      // A store's shifts can start as a copy of another store's (ADR 0038).
      'store.shift_definition': { listToolbar: (list) => <CopyShiftsToolbar refresh={list.refresh} /> },
      // Every store of a brand on an online channel at once (FR-ONL-07).
      'online.channel_store': { listToolbar: (list) => <AddChannelStoresButton refresh={list.refresh} /> },
      // Print templates (FR-CFG-12, ADR 0047): the text beside a live preview on a made-up document, on a tab with room for both.
      'config.document_template': {
        fieldTabs: ['body'],
        fieldEditors: { body: (field) => <TemplateBodyEditor {...field} /> },
      },
      'config.pos_profile': {
        // A copy is a starting point for one store's own profile, with its own POS layout.
        actions: (profile) => <DuplicateButton resource="config.pos_profile" record={profile} />,
        // Every button the POS draws comes with a new profile; the Buttons tab can reset them to that set.
        childToolbars: {
          'config.pos_button': (profile, list) => <PosButtonsToolbar profile={profile} refresh={list.refresh} />,
        },
        tabs: [{ label: t('posSync.tab'), render: (profile) => <PosProfileSyncTab profile={profile} /> }],
        // Settings are switches the POS reads: checkboxes, not raw JSON.
        fieldEditors: { settings: (field) => <ProfileSettingsEditor {...field} /> },
      },
    }),
    [t, session],
  );

  if (!session)
    return (
      <LoginPage
        onSubmit={hoLogin.login}
        sso={hoLogin.sso}
        secondFactor={hoLogin.secondFactor}
        notice={hoLogin.notice}
      />
    );

  const logOff = async () => {
    await hoApi.POST('/api/v1/identity/sessions/logout').catch(() => undefined);
    useSession.getState().end();
  };

  // At HO the choice is also saved to the user's profile (FR-ORG-07) and reaches stores through sync.
  const changeLanguage = (language: Language) => {
    setLanguage(language);
    void hoApi.PUT('/api/v1/identity/me/language', { body: { language } }).catch(() => undefined);
  };

  return (
    <AdminProvider
      resources={HO_RESOURCES}
      client={hoApi}
      session={session}
      extensions={extensions}
      addressSource={addressSource}
      addressLookup={addressLookupEnabled.data ? addressLookup : undefined}
      textLanguages={textLanguages.data}
      history={HO_HISTORY}
    >
      <AdminShell
        title={t('admin.ho.title')}
        context={t('admin.account.tenant', { code: TENANT_CODE })}
        nav={nav}
        session={session}
        onLogOff={() => void logOff()}
        language={currentLanguage()}
        onLanguageChange={changeLanguage}
        helpHref="/docs"
        accountLinks={[{ label: t('signIn.twoStep.title'), path: '/account/two-step' }]}
        notice={
          session.needsMfa && (
            <InlineNotification
              kind="warning"
              lowContrast
              hideCloseButton
              title={t('signIn.twoStep.needed')}
              subtitle={t('signIn.twoStep.neededHint')}
            />
          )
        }
      >
        <Routes>
          <Route path="/" element={<HoOverview />} />
          {orderRoutes('ho')}
          {kitchenRoutes('ho')}
          <Route path="/delivery/services" element={<DeliveryServicesPage />} />
          <Route path="/delivery/providers" element={<DeliveryProvidersPage />} />
          <Route path="/einvoice/invoices" element={<EinvoicesPage />} />
          <Route path="/einvoice/invoices/:id" element={<EinvoiceDetailPage />} />
          <Route path="/einvoice/providers" element={<ProviderAccountsPage capability="einvoice" />} />
          <Route path="/locations/import" element={<LocationDatasetsPage />} />
          <Route path="/sync/publish" element={<MasterDataPublishPage />} />
          <Route path="/sync/stores" element={<StoreConnectionsPage />} />
          <Route path="/org/branding" element={<BrandingPage />} />
          <Route path="/org/audit-settings" element={<AuditSettingsPage />} />
          <Route path="/org/sign-in" element={<SsoSettingsPage />} />
          <Route path="/account/two-step" element={<TwoStepPage />} />
          <Route path="/sync/logs" element={<SyncLogsPage />} />
          <Route path="/integration/logs" element={<IntegrationLogsPage />} />
          <Route path="/audit" element={<AuditLogPage />} />
          <Route path="/tables/quick-setup" element={<QuickSetupPage />} />
          <Route path="/loyalty/points" element={<MemberPointsPage />} />
          <Route path="/loyalty/issue-rewards" element={<IssueRewardsPage />} />
          <Route path="/cms/images" element={<ImagesPage />} />
          <Route path="/cms/images" element={<Navigate to="/cms/images" replace />} />
          <Route path="/images" element={<Navigate to="/cms/images" replace />} />
          <Route path="/online/orders" element={<OnlineOrdersPage />} />
          <Route path="/online/customer-security" element={<CustomerSecurityPage />} />
          <Route path="/online/orders/:id" element={<OnlineOrderPage />} />
          <Route path="/online/table-qr" element={<TableQrPage />} />
          <Route path="/catalog/translations" element={<TranslationsPage />} />
          <Route path="/catalog/import" element={<ImportPage />} />
          <Route path="/online/message-templates" element={<MessageTemplatesPage />} />
          <Route path="/online/customer-messages" element={<CustomerMessagesPage />} />
          {FORMS_DEF && <Route path="/cms/forms" element={<ListScreen def={FORMS_DEF} fixed={FORMS_FIXED} />} />}
          <Route path="/cms/submissions" element={<FormSubmissionsPage />} />
          <Route path="/cms/submissions/:id" element={<FormSubmissionPage />} />
          <Route path="/online/customers" element={<CustomerAccountsPage />} />
          <Route path="/online/customers/:id" element={<CustomerAccountPage />} />
          <Route path="/online/messaging-accounts" element={<ProviderAccountsPage capability="messaging" />} />
          <Route path="/payments/accounts" element={<ProviderAccountsPage capability="payment" />} />
          <Route path="/payments/profiles" element={<PaymentProfilesPage />} />
          <Route path="/payments/reconciliation" element={<PaymentReconciliationPage />} />
          <Route path={REPORT_PATHS.sales} element={<SalesReportPage />} />
          <Route path={REPORT_PATHS.products} element={<ProductMixPage />} />
          <Route path={REPORT_PATHS.hours} element={<HourlyReportPage />} />
          <Route path={REPORT_PATHS.payments} element={<PaymentsTaxReportPage />} />
          <Route path={REPORT_PATHS.qr} element={<QrReconciliationPage />} />
          <Route path={REPORT_PATHS.controls} element={<ControlsReportPage />} />
          <Route path={REPORT_PATHS.journal} element={<JournalReportPage />} />
          <Route path={REPORT_PATHS.einvoices} element={<EinvoiceReconPage />} />
          <Route path={REPORT_PATHS.menu} element={<MenuEngineeringPage />} />
          <Route path={REPORT_PATHS.staff} element={<StaffReportPage />} />
          <Route path={REPORT_PATHS.operations} element={<OperationsReportPage />} />
          <Route path={REPORT_PATHS.customers} element={<CustomersReportPage />} />
          <Route path={HO_STOCK_PATHS.balances} element={<HoStockBalancesPage />} />
          <Route path={HO_STOCK_PATHS.movements} element={<HoStockMovementsPage />} />
          <Route path={HO_STOCK_PATHS.transfers} element={<HoStockTransfersPage />} />
          <Route path={HO_STOCK_PATHS.counts} element={<HoStockCountsPage />} />
          {resourceRoutes(HO_RESOURCES)}
        </Routes>
      </AdminShell>
    </AdminProvider>
  );
}
